import { itemTokens, tokensIn } from "../items/description.ts";
import { fieldsOf, foreignMessage, foreignValue, iconOf, isPlainRecord, listOf, oneOf, pathTo, report, whole, type Place } from "./checks.ts";
import { GLYPH_NAME_PATTERN, ITEM_ID_PATTERN, ITEM_KINDS, ITEM_RARITIES, ITEM_TAGS, MAX_ITEM_DESCRIPTION_CHARS, MAX_ITEM_ID_CHARS, MAX_ITEM_NAME_CHARS, MAX_ITEM_STACK, RULES_ID_PATTERN, TOOL_KINDS, type Ability, type Item, type ItemTag, type RulesProblem, type ToolKind } from "./types.ts";

/**
 * The items table's record check (ADR-091 decision 1): an item's id, name, kind, rarity (ADR-125 decision 1.1), stack, use, sound, glyph, icon and
 * description, each held to its bounds, and its use held to an ability record the abilities table holds, that no
 * character is given and no weapon names, so no bar holds it and `act` refuses it, and whose shape is `self`, the one
 * shape the item path lands. An item's id is longer than a rules id (`MAX_ITEM_ID_CHARS`, the bound Postgres checks),
 * so the table names its records by its own pattern. Its description names only tokens its own stats supply (ADR-091's
 * amendment of 2026-10-09).
 */

const ITEM_FIELDS = ["id", "name", "kind", "rarity", "stack", "use", "sound", "glyph", "icon", "description"] as const;

/**
 * The fields a record may leave out: `tags` (ADR-142 decision 3a) and `tool`. A record that holds one holds it checked,
 * and the parsed item carries it only then.
 */
const OPTIONAL_ITEM_FIELDS = ["tags", "tool"] as const;

/**
 * What an item's use is checked against: the ids the abilities table holds, those every character is given, those
 * whose shape is `self`, the weapon that names each ability a weapon names, and the records that passed their own
 * checks, whose stats supply a description's tokens.
 */
export interface UseTargets {
  readonly abilityIds: ReadonlySet<string>;
  readonly records: ReadonlyMap<string, Ability>;
  readonly givenIds: ReadonlySet<string>;
  readonly selfIds: ReadonlySet<string>;
  readonly weaponOf: ReadonlyMap<string, string>;
}

/**
 * The name an item goes by in its problems: its id when that is well formed, else its place. The field is read
 * through its descriptor, so no getter runs.
 *
 * @param value The record.
 * @param index Its place in the table.
 * @returns The name, or `#n` for the n-th record.
 */
function itemName(value: unknown, index: number): string {
  const named: unknown = isPlainRecord(value) ? Object.getOwnPropertyDescriptor(value, "id")?.value : undefined;
  return typeof named === "string" && ITEM_ID_PATTERN.test(named) ? named : `#${index + 1}`;
}

/**
 * Reads a text a player reads: 1 to `max` characters.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param max The most characters it may hold.
 * @returns The text, or null.
 */
function text(place: Place, value: unknown, path: string, max: number): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    report(place, `${path} is not a string`);
    return null;
  }
  if (value.length < 1 || value.length > max) {
    report(place, `${path} is ${value.length} characters, not 1 to ${max}`);
    return null;
  }
  return value;
}

/**
 * Reads a name that is null or matches a pattern.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param pattern The pattern.
 * @param what What such a name is, for `is not`.
 * @returns The name, null for none, or undefined when it is missing or wrong.
 */
function nullableName(place: Place, value: unknown, path: string, pattern: RegExp, what: string): string | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== "string" || !pattern.test(value)) {
    report(place, `${path} ${String(value)} is not ${what}`);
    return undefined;
  }
  return value;
}

/**
 * Checks an item's use against its kind: a consumable names an ability record the abilities table holds, given to no
 * character, named by no weapon and aimed at oneself alone (a `self` shape, the only one the item path lands); every
 * other kind names none.
 *
 * @param place Where to report.
 * @param value The use.
 * @param kind The item's kind, null when it was wrong.
 * @param targets What a use is checked against.
 * @returns The record's id, null for none, or undefined when it is missing or wrong.
 */
function useOf(place: Place, value: unknown, kind: Item["kind"] | null, targets: UseTargets): string | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value !== null && typeof value !== "string") {
    report(place, "use is neither null nor a string");
    return undefined;
  }
  if (value === null) {
    if (kind === "consumable") {
      report(place, "a consumable names the ability record its use runs");
      return undefined;
    }
    return null;
  }
  if (kind !== null && kind !== "consumable") {
    report(place, `use is ${value}, and only a consumable has a use`);
    return undefined;
  }
  if (!targets.abilityIds.has(value)) {
    report(place, `use names ${value}, which the abilities table does not hold`);
    return undefined;
  }
  if (targets.givenIds.has(value)) {
    report(place, `use names ${value}, which every character is given`);
    return undefined;
  }
  const weapon = targets.weaponOf.get(value);
  if (weapon !== undefined) {
    report(place, `use names ${value}, which the weapon ${weapon} names`);
    return undefined;
  }
  if (!targets.selfIds.has(value)) {
    report(place, `use names ${value}, whose shape is not self, and an item's use runs a record aimed at oneself alone`);
    return undefined;
  }
  return value;
}

/**
 * Checks an item's tags: a non-empty list of distinct tags of the closed set, or absent.
 *
 * @param place Where to report.
 * @param value The `tags` field, undefined when the record leaves it out.
 * @returns The tags, null for an item with none, or undefined when the field is wrong.
 */
function tagsOf(place: Place, value: unknown): readonly ItemTag[] | null | undefined {
  if (value === undefined) {
    return null;
  }
  const list = listOf(place, value, "tags");
  if (list === null) {
    return undefined;
  }
  if (list.length === 0) {
    report(place, "tags is empty; leave the field out for an item with no tag");
    return undefined;
  }
  const tags: ItemTag[] = [];
  let ok = true;
  for (const [index, entry] of list.entries()) {
    const tag = oneOf(place, entry, pathTo("tags", index, true), ITEM_TAGS, "an item tag");
    if (tag === null) {
      ok = false;
    } else if (tags.includes(tag)) {
      report(place, `${pathTo("tags", index, true)} is ${tag}, which ${pathTo("tags", tags.indexOf(tag), true)} already names`);
      ok = false;
    } else {
      tags.push(tag);
    }
  }
  return ok ? tags : undefined;
}

/**
 * Checks an item's tool kind: one of the closed set on an equipment item, or absent.
 *
 * @param place Where to report.
 * @param value The `tool` field, undefined when the record leaves it out.
 * @param kind The item's kind, null when it was wrong.
 * @returns The kind, null for an item that is no tool, or undefined when the field is wrong.
 */
function toolOf(place: Place, value: unknown, kind: Item["kind"] | null): ToolKind | null | undefined {
  if (value === undefined) {
    return null;
  }
  const tool = oneOf(place, value, "tool", TOOL_KINDS, "a tool kind");
  if (tool === null) {
    return undefined;
  }
  if (kind !== null && kind !== "equipment") {
    report(place, `tool is ${tool}, and only equipment is a tool`);
    return undefined;
  }
  return tool;
}

/**
 * Checks one item.
 *
 * @param place Where to report.
 * @param record The record.
 * @param targets What its use is checked against.
 * @returns The item, or null.
 */
function itemOf(place: Place, record: Readonly<Record<string, unknown>>, targets: UseTargets): Item | null {
  const required = Object.fromEntries(Object.entries(record).filter(([key]) => !(OPTIONAL_ITEM_FIELDS as readonly string[]).includes(key)));
  const fields = fieldsOf(place, required, "", ITEM_FIELDS, "an item") as Readonly<Record<string, unknown>>;
  const rawId = fields["id"];
  let id: string | null = null;
  if (typeof rawId === "string" && ITEM_ID_PATTERN.test(rawId)) {
    id = rawId;
  } else if (rawId !== undefined) {
    report(place, typeof rawId === "string" ? `id ${rawId} is not 1 to ${MAX_ITEM_ID_CHARS} characters of a-z, 0-9 and _` : "id is not a string");
  }
  const itemName = text(place, fields["name"], "name", MAX_ITEM_NAME_CHARS);
  const kind = oneOf(place, fields["kind"], "kind", ITEM_KINDS, "an item kind");
  const rarity = oneOf(place, fields["rarity"], "rarity", ITEM_RARITIES, "an item rarity");
  let stack = whole(place, fields["stack"], "stack", 1, MAX_ITEM_STACK);
  if (kind === "equipment" && stack !== null && stack !== 1) {
    report(place, `equipment stacks to 1, and stack is ${stack}`);
    stack = null;
  }
  const use = useOf(place, fields["use"], kind, targets);
  const sound = nullableName(place, fields["sound"], "sound", RULES_ID_PATTERN, "a sound set's name (1 to 32 characters of a-z, 0-9 and _ starting with a letter) or null");
  const glyph = nullableName(place, fields["glyph"], "glyph", GLYPH_NAME_PATTERN, "a glyph's name (1 to 32 characters of a-z, 0-9 and - starting with a letter) or null");
  const icon = iconOf(place, fields["icon"]);
  const description = text(place, fields["description"], "description", MAX_ITEM_DESCRIPTION_CHARS);
  const tags = tagsOf(place, record["tags"]);
  const tool = toolOf(place, record["tool"], kind);
  if (tags === undefined || tool === undefined) {
    return null;
  }
  if (id === null || itemName === null || kind === null || rarity === null || stack === null || use === undefined || sound === undefined || glyph === undefined || icon === undefined || description === null || Object.keys(fields).length !== ITEM_FIELDS.length) {
    return null;
  }
  const item: Item = { id, name: itemName, kind, rarity, stack, use, sound, glyph, icon, description, ...(tags === null ? {} : { tags }), ...(tool === null ? {} : { tool }) };
  return descriptionTokensHeld(place, item, targets) ? item : null;
}

/**
 * Checks that an item's description names only tokens its own stats supply (ADR-091's amendment of 2026-10-09, item
 * 4), and holds no brace outside a token. A stray brace is refused whatever the use; the tokens of an item whose use
 * names a record that failed its own checks are not judged, since that record's problem is already reported.
 *
 * @param place Where to report.
 * @param item The item, its other fields checked.
 * @param targets What its use is checked against, with the records that passed.
 * @returns Whether the description passed, or its tokens were not judged.
 */
function descriptionTokensHeld(place: Place, item: Item, targets: UseTargets): boolean {
  const named = tokensIn(item.description);
  if (!named.ok) {
    report(place, `the description holds the brace in "${named.stray}" outside a token; a token is a name such as {restore.health}, and a brace is never prose`);
    return false;
  }
  const use = item.use === null ? null : targets.records.get(item.use);
  if (use === undefined) {
    return true;
  }
  const supplied = itemTokens(item, use).map(({ name }) => name);
  const unknown = named.names.find((name) => !supplied.includes(name));
  if (unknown === undefined) {
    return true;
  }
  const braced = supplied.map((name) => `{${name}}`);
  const offered = braced.length <= 1 ? (braced[0] ?? "none") : `${braced.slice(0, -1).join(", ")} and ${braced[braced.length - 1] as string}`;
  report(place, `the description names {${unknown}}, which ${item.name}'s stats do not supply; they supply ${offered}`);
  return false;
}

/**
 * Checks each record of the items table in turn: a value that is not JSON data first, then the record's own fields,
 * then that its id is not held twice.
 *
 * @param records The items table.
 * @param problems Where problems go.
 * @param targets What a use is checked against.
 * @returns Each item that passed, a second of one id left out.
 */
export function checkItems(records: readonly unknown[], problems: RulesProblem[], targets: UseTargets): Item[] {
  const passed: Item[] = [];
  const seen = new Set<string>();
  for (const [index, value] of records.entries()) {
    const recordId = itemName(value, index);
    const place: Place = { table: "items", id: recordId, problems };
    const foreign = foreignValue(value, "");
    if (foreign !== null) {
      report(place, foreignMessage(foreign, "the record"));
      continue;
    }
    if (!isPlainRecord(value)) {
      report(place, "it is not a record of fields");
      continue;
    }
    const built = itemOf(place, value, targets);
    if (!recordId.startsWith("#") && seen.has(recordId)) {
      report(place, "the id is held twice");
      continue;
    }
    seen.add(recordId);
    if (built !== null) {
      passed.push(built);
    }
  }
  return passed;
}
